<template>
  <div class="glass-panel p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white">Cost Trend</h2>
      <div class="flex gap-2">
        <button 
          v-for="range in timeRanges" 
          :key="range"
          @click="selectedRange = range"
          :class="selectedRange === range ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'"
          class="px-3 py-1 rounded-lg text-sm font-medium transition-colors hover:bg-purple-500 hover:text-white"
        >
          {{ range }}
        </button>
      </div>
    </div>
    
    <div class="h-80">
      <Line v-if="chartData" :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const props = defineProps<{
  data: Array<{
    date: string
    computeCost: number
    storageCost: number
    dataTransferCost: number
    cloudServicesCost: number
  }>
}>()

const timeRanges = ['7d', '30d', '90d', '1y']
const selectedRange = ref('30d')

const chartData = computed(() => {
  if (!props.data || props.data.length === 0) return null
  
  return {
    labels: props.data.map(d => d.date),
    datasets: [
      {
        label: 'Compute',
        data: props.data.map(d => d.computeCost),
        backgroundColor: 'rgba(139, 92, 246, 0.3)',
        borderColor: 'rgb(139, 92, 246)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Storage',
        data: props.data.map(d => d.storageCost),
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Data Transfer',
        data: props.data.map(d => d.dataTransferCost),
        backgroundColor: 'rgba(6, 182, 212, 0.3)',
        borderColor: 'rgb(6, 182, 212)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Cloud Services',
        data: props.data.map(d => d.cloudServicesCost),
        backgroundColor: 'rgba(20, 184, 166, 0.3)',
        borderColor: 'rgb(20, 184, 166)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: {
        color: '#9CA3AF',
        usePointStyle: true,
        padding: 15
      }
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
      backgroundColor: 'rgba(17, 24, 39, 0.9)',
      titleColor: '#F3F4F6',
      bodyColor: '#D1D5DB',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      borderWidth: 1,
      padding: 12,
      callbacks: {
        label: function(context: any) {
          let label = context.dataset.label || ''
          if (label) {
            label += ': '
          }
          label += '$' + context.parsed.y.toFixed(2)
          return label
        }
      }
    }
  },
  scales: {
    x: {
      stacked: true,
      grid: {
        color: 'rgba(156, 163, 175, 0.1)'
      },
      ticks: {
        color: '#9CA3AF'
      }
    },
    y: {
      stacked: true,
      grid: {
        color: 'rgba(156, 163, 175, 0.1)'
      },
      ticks: {
        color: '#9CA3AF',
        callback: function(value: any) {
          return '$' + value
        }
      }
    }
  },
  interaction: {
    mode: 'nearest' as const,
    axis: 'x' as const,
    intersect: false
  }
}
</script>
